<script setup lang="ts">
import { RouterView, useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { onMounted, computed } from 'vue'
import Navbar from '@/components/layout/Navbar.vue'
import DashboardLayout from '@/layouts/DashboardLayout.vue'
import { Toaster } from '@/components/ui/toast'
import { useToast } from '@/composables/useToast'

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const { toast } = useToast()

// Pages that use the dashboard layout (authenticated pages with sidebar)
const dashboardRoutes = ['/dashboard', '/tours', '/statistics', '/leads', '/settings', '/usage']

const useDashboardLayout = computed(() => {
  return authStore.isAuthenticated && dashboardRoutes.some(r => route.path.startsWith(r))
})

// Admin pages use their own AdminLayout — no Navbar wrapper needed
const isAdminRoute = computed(() => route.path.startsWith('/admin'))

const BUILDER_ERRORS: Record<string, string> = {
  session_expired: 'The builder link has expired. Please generate a new one from your CRM and try again.',
  session_used:    'This builder link has already been used. Please generate a new one from your CRM.',
  server_error:    'Something went wrong opening the builder. Please try again.',
}

onMounted(async () => {
  // Try to restore session on app load
  await authStore.initialize()

  // Handle builder session errors redirected back from /auth/builder-token
  const builderError = route.query.builder_error as string | undefined
  if (builderError && BUILDER_ERRORS[builderError]) {
    // Strip param from URL cleanly
    router.replace({ path: route.path, query: { ...route.query, builder_error: undefined } })
    // Show toast after a short delay so the router transition settles
    setTimeout(() => {
      toast({
        title: 'Builder link expired',
        description: BUILDER_ERRORS[builderError],
        variant: 'destructive',
      })
    }, 300)
  }
})
</script>

<template>
  <div class="min-h-screen">
    <!-- Admin pages: standalone AdminLayout, no Navbar -->
    <template v-if="isAdminRoute">
      <RouterView />
    </template>

    <!-- Dashboard layout for authenticated app pages -->
    <template v-else-if="useDashboardLayout">
      <DashboardLayout>
        <RouterView />
      </DashboardLayout>
    </template>

    <!-- Public layout for auth pages and landing -->
    <template v-else>
      <div class="flex flex-col min-h-screen">
        <Navbar />
        <main class="flex-1">
          <RouterView />
        </main>
      </div>
    </template>

    <Toaster />
  </div>
</template>
